/**
 * Gewohnheiten samt berechneter Kennzahlen.
 *
 * Streaks und Quoten werden bei jedem Rendern neu gerechnet - nie gespeichert.
 * Bei der Groessenordnung eines persoenlichen Verlaufs (ein paar tausend
 * Eintraege) ist das deutlich unter einer Millisekunde.
 */
import { useLiveQuery } from 'dexie-react-hooks';
import { useMemo } from 'react';

import { db } from '@/db/schema';
import { completionRate, computeStreak, type StreakResult } from '@/domain/habits/streak';
import { useAreaFilter } from '@/store/areaFilter';
import { addDays, today } from '@/lib/datetime';
import type { Habit, ID, LocalDate } from '@/domain/types';

/** So weit zurueck reicht die Heatmap - 13 Wochen. */
export const HEATMAP_DAYS = 91;

export interface HabitWithStats {
  habit: Habit;
  /** Erledigte Tage im Heatmap-Zeitraum. */
  done: Set<LocalDate>;
  streak: StreakResult;
  rate: ReturnType<typeof completionRate>;
}

/**
 * `nurBereich`: nur die Gewohnheiten dieses Bereichs, unabhaengig vom
 * globalen Bereichsfilter (Bereichs-Board, F-01).
 */
export function useHabitsWithStats(includeArchived = false, nurBereich?: ID): HabitWithStats[] {
  const heute = today();
  const von = addDays(heute, -(HEATMAP_DAYS - 1));

  const { activeAreaIds } = useAreaFilter();

  const rows = useLiveQuery(
    async () => {
      const habits = await db.habits.orderBy('order').toArray();
      const entries = await db.habitEntries
        .where('date').between(von, heute, true, true)
        .toArray();

      const byHabit = new Map<string, Set<LocalDate>>();
      for (const e of entries) {
        if (!e.done) continue;
        const set = byHabit.get(e.habitId) ?? new Set<LocalDate>();
        set.add(e.date);
        byHabit.set(e.habitId, set);
      }

      return habits.map((habit) => ({
        habit,
        done: byHabit.get(habit.id) ?? new Set<LocalDate>(),
      }));
    },
    [von, heute],
    [] as { habit: Habit; done: Set<LocalDate> }[],
  );

  return useMemo(() => rows
    .filter((r) => (includeArchived || !r.habit.archived))
    .filter((r) => (nurBereich
      ? r.habit.areaId === nurBereich
      : activeAreaIds.length === 0 || (r.habit.areaId != null && activeAreaIds.includes(r.habit.areaId))))
    .map(({ habit, done }) => ({
      habit,
      done,
      streak: computeStreak(done, habit.cadence, heute),
      rate: completionRate(done, habit.cadence, heute, 30),
    })),
  [rows, includeArchived, activeAreaIds, heute, nurBereich]);
}
